import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  PhotoIcon, 
  VideoCameraIcon 
} from '@heroicons/react/24/outline';

const Media = () => {
  const mediaSections = [
    {
      title: "პრეს სამსახური",
      description: "პრეს რელიზები, კონფერენციები და ოფიციალური განცხადებები",
      icon: DocumentTextIcon,
      href: "/media/press",
      color: "bg-blue-500"
    },
    {
      title: "ფოტო გალერეა",
      description: "მატჩების, ტრენინგებისა და ღონისძიებების ფოტოები",
      icon: PhotoIcon,
      href: "/media/photos",
      color: "bg-green-500"
    },
    {
      title: "ვიდეო გალერეა",
      description: "მატჩების ჰაილაითები, ინტერვიუები და სპეციალური ვიდეოები",
      icon: VideoCameraIcon,
      href: "/media/videos",
      color: "bg-purple-500"
    }
  ];

  const recentPhotos = [
    { id: 1, title: "მატჩი vs დინამო ბათუმი", date: "10 სექტემბერი, 2024" },
    { id: 2, title: "ტრენინგი", date: "8 სექტემბერი, 2024" },
    { id: 3, title: "ღონისძიება", date: "5 სექტემბერი, 2024" },
    { id: 4, title: "ახალი ფეხბურთელის წარდგენა", date: "3 სექტემბერი, 2024" }
  ];

  const recentVideos = [
    { id: 1, title: "მატჩის ჰაილაითები", duration: "5:30", date: "10 სექტემბერი, 2024" },
    { id: 2, title: "მწვრთნელის ინტერვიუ", duration: "3:45", date: "8 სექტემბერი, 2024" },
    { id: 3, title: "ფეხბურთელის ინტერვიუ", duration: "4:20", date: "5 სექტემბერი, 2024" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            მედია
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი კლუბის უახლესი ამბები, ფოტოები და ვიდეოები
          </p>
        </div>

        {/* Media Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {mediaSections.map((section) => (
            <Link
              key={section.title}
              to={section.href}
              className="group bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="text-center">
                <div className={`${section.color} p-4 rounded-full w-16 h-16 mx-auto mb-4`}>
                  <section.icon className="h-8 w-8 text-white mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 font-georgian mb-2 group-hover:text-primary-600 transition-colors">
                  {section.title}
                </h3>
                <p className="text-gray-600 font-georgian">
                  {section.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Recent Photos */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 font-georgian">
              ბოლო ფოტოები
            </h2>
            <Link
              to="/media/photos"
              className="text-primary-600 hover:text-primary-700 font-medium font-georgian"
            >
              ყველა ფოტო
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {recentPhotos.map((photo) => (
              <div key={photo.id} className="bg-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-w-16 aspect-h-12 bg-gray-200 flex items-center justify-center">
                  <PhotoIcon className="h-12 w-12 text-gray-400" />
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 font-georgian text-sm mb-1">
                    {photo.title}
                  </h4>
                  <p className="text-xs text-gray-600 font-georgian">
                    {photo.date}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Videos */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 font-georgian">
              ბოლო ვიდეოები
            </h2>
            <Link
              to="/media/videos"
              className="text-primary-600 hover:text-primary-700 font-medium font-georgian"
            >
              ყველა ვიდეო
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentVideos.map((video) => (
              <div key={video.id} className="bg-gray-100 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-w-16 aspect-h-9 bg-gray-200 flex items-center justify-center">
                  <VideoCameraIcon className="h-12 w-12 text-gray-400" />
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 font-georgian text-sm mb-1">
                    {video.title}
                  </h4>
                  <div className="flex justify-between items-center text-xs text-gray-600 font-georgian">
                    <span>{video.duration}</span>
                    <span>{video.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Press Contact */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            პრეს კონტაქტი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
                პრეს სამსახური
              </h3>
              <div className="space-y-3 text-gray-600 font-georgian">
                <p><strong>ელ. ფოსტა:</strong> press@https://fciveria.ge</p>
                <p><strong>ტელეფონი:</strong> +995 32 123 4567</p>
                <p><strong>მობილური:</strong> +995 599 123 456</p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
                მედია მასალები
              </h3>
              <p className="text-gray-600 font-georgian mb-4">
                ჩვენი კლუბის ლოგოები, ფოტოები და სხვა მედია მასალები 
                ხელმისაწვდომია ჩამოსატვირთად.
              </p>
              <button className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 font-georgian">
                მედია კიტის ჩამოტვირთვა
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Media;
